import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ScrollToTop } from "./components/ScrollToTop";
import { MouseGradient } from "./components/MouseGradient";
import { SEO } from "./components/SEO";
import "./App.css";

// Code-split all page components
const Home = React.lazy(() => import("./pages/Home"));
const Services = React.lazy(() => import("./pages/Services"));
const CaseStudies = React.lazy(() => import("./pages/CaseStudies"));
const HowWeWork = React.lazy(() => import("./pages/HowWeWork"));
const About = React.lazy(() => import("./pages/About"));
const FunFacts = React.lazy(() => import("./pages/FunFacts"));
const Contact = React.lazy(() => import("./pages/Contact"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const ROUTE_SEO = {
  '/privacy-policy': { title: 'Privacy Policy', description: 'Read The Breez Media Privacy Policy. Learn how we collect, use, and protect your personal data across our website and services.' },
  '/terms-of-service': { title: 'Terms of Service', description: 'Read The Breez Media Terms of Service. Understand the terms governing use of our website, services, consultations, and digital products.' }
};

const RouteSEO = () => {
  const { pathname } = useLocation();
  const seo = ROUTE_SEO[pathname];
  if (!seo) return null;
  return <SEO title={seo.title} path={pathname} description={seo.description} />;
};

// Track pageviews on every route change
const GATracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-7S370HPR9Q', { page_path: pathname });
    }
  }, [pathname]);
  return null;
};

// Loading skeleton matching site branding
const PageLoader = () => (
  <div className="min-h-screen bg-[#05051e] flex items-center justify-center" data-testid="page-loader">
    <div className="text-center">
      <img src="/logo.png" alt="The Breez Media" className="h-12 mx-auto mb-6 opacity-80" />
      <div className="flex gap-1.5 justify-center">
        <div className="w-2 h-2 rounded-full bg-[#ffcd04] animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 rounded-full bg-[#ffcd04] animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 rounded-full bg-[#ffcd04] animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    removeBadge();
    const interval = setInterval(removeBadge, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#05051e]">
      <BrowserRouter>
        <RouteSEO />
        <GATracker />
        <MouseGradient />
        <ScrollToTop />
        <Navbar />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/about" element={<About />} />
            <Route path="/funfacts" element={<FunFacts />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
